import { useState } from 'react';
import { Link } from 'react-router-dom';
import { addDoc, collection, serverTimestamp } from 'firebase/firestore';
import { getDownloadURL, ref, uploadBytes } from 'firebase/storage';
import { Plus, Upload } from 'lucide-react';
import { toast } from 'sonner';
import { db, storage } from '../../../firebase/config';
import { useAuth } from '../../../hooks/useAuth';
import { Button } from '../../ui/button';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../../ui/dialog';
import { Input } from '../../ui/input';
import { NativeSelect } from '../../ui/select-native';
import { Textarea } from '../../ui/textarea';

const DIFFICULTY_LABELS = {
  1: 'Easy',
  2: 'Medium',
  3: 'Challenging',
  4: 'Hard',
  5: 'Very hard',
};

const EMPTY = {
  mapName: '',
  mapper: '',
  tier: 1,
  difficulty: 1,
  hintText: '',
};

const Field = ({ label, required, hint, children }) => (
  <div className="flex flex-col gap-2">
    <span className="text-xs font-medium uppercase tracking-[0.15em] text-muted-foreground">
      {label}
      {required && <span className="ml-1 text-heat">*</span>}
    </span>
    {children}
    {hint && <span className="text-xs text-muted-foreground">{hint}</span>}
  </div>
);

const SubmitMapDialog = () => {
  const { currentUser, userProfile } = useAuth();
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [saving, setSaving] = useState(false);

  const loggedIn = !!currentUser;

  const reset = () => {
    setForm(EMPTY);
    setFile(null);
    setPreview(null);
  };

  const pickFile = (f) => {
    if (!f) return;
    setFile(f);
    setPreview(URL.createObjectURL(f));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!loggedIn) return;
    if (!form.mapName.trim()) {
      toast.error('Map name is required.');
      return;
    }
    if (!file) {
      toast.error('Pick a screenshot of the map.');
      return;
    }
    setSaving(true);
    try {
      const path = `map-submissions/${Date.now()}_${file.name}`;
      const sref = ref(storage, path);
      await uploadBytes(sref, file);
      const imageUrl = await getDownloadURL(sref);

      await addDoc(collection(db, 'mapSubmissions'), {
        mapName: form.mapName.trim(),
        mapper: form.mapper.trim(),
        tier: Number(form.tier) || 1,
        difficulty: Number(form.difficulty) || 1,
        hintText: form.hintText.trim(),
        imageUrl,
        storageFilePath: path,
        status: 'pending',
        submittedBy: currentUser.uid,
        submitterName: userProfile?.username || currentUser.displayName || 'Anonymous',
        submitterEmail: currentUser.email || null,
        createdAt: serverTimestamp(),
      });

      toast.success('Thanks! Your submission is waiting for admin review.');
      reset();
      setOpen(false);
    } catch (err) {
      toast.error('Submission failed.', { description: err.message });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(v) => {
        setOpen(v);
        if (!v) reset();
      }}
    >
      <DialogTrigger asChild>
        <Button variant="outline" size="sm" className="font-mono uppercase tracking-[0.2em]">
          <Plus className="h-4 w-4" />
          Submit a map
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="font-display">Submit a map image</DialogTitle>
        </DialogHeader>

        {!loggedIn ? (
          <div className="flex flex-col gap-4 py-2">
            <p className="text-sm text-muted-foreground">
              Log in to submit your own KZ map screenshots for the daily game. An admin will review
              every submission before it goes live.
            </p>
            <div className="flex gap-2">
              <Button asChild>
                <Link to="/login">Log in</Link>
              </Button>
              <Button asChild variant="ghost">
                <Link to="/signup">Sign up</Link>
              </Button>
            </div>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="grid gap-4">
            <p className="text-xs text-muted-foreground">
              Uploads stay hidden until an admin approves them. Please submit clean in-game
              screenshots only — no watermarks, UI, or spoiler text.
            </p>

            <Field label="Map name" required>
              <Input
                value={form.mapName}
                onChange={(e) => setForm({ ...form, mapName: e.target.value })}
                placeholder="kz_example"
                required
              />
            </Field>

            <Field label="Mapper" hint="Author credit — used as an in-game hint.">
              <Input
                value={form.mapper}
                onChange={(e) => setForm({ ...form, mapper: e.target.value })}
                placeholder="e.g. GameChaos"
              />
            </Field>

            <div className="grid gap-4 sm:grid-cols-2">
              <Field label="Tier" hint="KZ map tier (1 easiest, 7 death).">
                <NativeSelect
                  value={form.tier}
                  onChange={(e) => setForm({ ...form, tier: Number(e.target.value) })}
                >
                  {[1, 2, 3, 4, 5, 6, 7].map((t) => (
                    <option key={t} value={t}>
                      Tier {t}
                    </option>
                  ))}
                </NativeSelect>
              </Field>
              <Field label="Guess difficulty" hint="How hard is it to name?">
                <NativeSelect
                  value={form.difficulty}
                  onChange={(e) => setForm({ ...form, difficulty: Number(e.target.value) })}
                >
                  {[1, 2, 3, 4, 5].map((lvl) => (
                    <option key={lvl} value={lvl}>
                      {lvl} — {DIFFICULTY_LABELS[lvl]}
                    </option>
                  ))}
                </NativeSelect>
              </Field>
            </div>

            <Field label="Field note (optional)" hint="Short trivia shown below the image.">
              <Textarea
                rows={2}
                value={form.hintText}
                onChange={(e) => setForm({ ...form, hintText: e.target.value })}
              />
            </Field>

            <Field label="Screenshot" required>
              <input
                type="file"
                accept="image/*"
                onChange={(e) => pickFile(e.target.files?.[0])}
                className="block w-full text-sm file:mr-4 file:rounded-md file:border-0 file:bg-secondary file:px-3 file:py-2 file:text-xs file:font-medium file:uppercase file:tracking-[0.15em] file:text-secondary-foreground hover:file:bg-secondary/80"
              />
              {preview && (
                <img
                  src={preview}
                  alt=""
                  className="mt-3 h-40 w-full rounded-md border border-border/60 object-cover"
                />
              )}
            </Field>

            <DialogFooter>
              <Button type="button" variant="ghost" onClick={() => setOpen(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={saving}>
                <Upload className="h-4 w-4" />
                {saving ? 'Submitting…' : 'Submit for review'}
              </Button>
            </DialogFooter>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default SubmitMapDialog;
